import { useCallback, useEffect, useState } from 'react';
import { FACTORY_TZ, localDate, type HourlyDto } from '@app/shared';
import { api } from '../api';
import { useApp } from '../state';
import { EventList, useNow } from './MyShift';

/** Производство по часам (онлайн): график выработки, план и сделано, простои, журнал событий. */
export function Hourly() {
  const { fail } = useApp();
  const [day, setDay] = useState(localDate(Date.now(), FACTORY_TZ));
  const [d, setD] = useState<HourlyDto | null>(null);
  const now = useNow(30_000);
  const load = useCallback(async () => {
    try {
      setD(await api.hourly(day));
    } catch (e) {
      fail(e);
    }
  }, [day, fail]);
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 60_000);
    return () => clearInterval(t);
  }, [load]);

  if (!d) return <div className="skeleton" />;
  // Показываем часы с выработкой и рабочие часы 08–19.
  const used = d.hours.map((h, i) => (h.qty > 0 ? i : -1)).filter((i) => i >= 0);
  const from = Math.min(8, ...used);
  const to = Math.max(19, ...used);
  const hours = d.hours.slice(from, to + 1);
  const max = Math.max(1, ...hours.map((h) => h.qty));
  const W = 340;
  const H = 150;
  const bw = W / hours.length;
  return (
    <div className="stack">
      <div className="row" style={{ gap: 8 }}>
        <input className="input" type="date" value={day} onChange={(e) => setDay(e.target.value)} aria-label="День" style={{ maxWidth: 180 }} />
        <span className="grow" />
        <span className="small muted">обновляется раз в минуту</span>
      </div>
      <div className="kpis">
        <div>
          <b>{d.total.toLocaleString('ru-RU')}</b>
          <span>сделано, шт.</span>
        </div>
        <div>
          <b>{d.plan ? d.plan.toLocaleString('ru-RU') : '—'}</b>
          <span>план смен</span>
        </div>
        <div>
          <b>{d.downtimeMinutes >= 60 ? `${Math.floor(d.downtimeMinutes / 60)} ч ${d.downtimeMinutes % 60}` : d.downtimeMinutes} мин</b>
          <span>простои</span>
        </div>
      </div>
      <figure className="card chart" style={{ margin: 0 }}>
        <svg viewBox={`0 0 ${W} ${H + 22}`} role="img" aria-label="Выработка по часам">
          {hours.map((h, i) => {
            const bh = (h.qty / max) * H;
            return (
              <g key={h.hour}>
                <rect x={i * bw + 3} y={H - bh} width={bw - 6} height={Math.max(bh, h.qty ? 2 : 0)} rx="3" className="bar" />
                {h.qty > 0 && (
                  <text x={i * bw + bw / 2} y={H - bh - 4} textAnchor="middle" className="bar-label">
                    {h.qty}
                  </text>
                )}
                <text x={i * bw + bw / 2} y={H + 16} textAnchor="middle" className="axis-label">
                  {h.hour.slice(0, 2)}
                </text>
              </g>
            );
          })}
          <line x1="0" x2={W} y1={H} y2={H} className="axis" />
        </svg>
        <figcaption className="small muted">Штук за каждый час (по отметкам операторов и закрытию смен)</figcaption>
      </figure>

      {d.tasks.length > 0 && (
        <>
          <h2 className="section-title">Задачи смен</h2>
          <div className="list">
            {d.tasks.map((t) => (
              <div key={t.id} className="item">
                <span className="grow stack" style={{ gap: 4 }}>
                  <span className="title ellipsis">{t.title}</span>
                  <span className="sub">
                    {t.done.toLocaleString('ru-RU')}
                    {t.qty !== null ? ` из ${t.qty.toLocaleString('ru-RU')}` : ''} шт.
                  </span>
                  {t.qty ? (
                    <span className="progress">
                      <i style={{ width: `${Math.round(Math.min(1, t.done / t.qty) * 100)}%` }} />
                    </span>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
      <h2 className="section-title">Журнал времени</h2>
      <EventList events={d.events} now={now} who />
    </div>
  );
}
